import { formatDate } from 'util/formatDate';
import React, { ReactElement, useEffect, useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useCoCurricularApi } from 'services/api/hooks';
import { LeadershipPosition } from 'services/api/apis/co-curricular/dto/leadership-positions.dto';
import { LeadershipPositionCard } from './LeadershipPositionCard';
import { SectionCountBadge } from './SectionCountBadge';

interface DisplayPosition {
  organization: string;
  position: string;
  startDate: string;
  endDate: string;
  logoUrl?: string;
}

const formatMonthYear = (date: string): string =>
  formatDate(new Date(date), { includeDate: false, includeYear: true });

const toDisplayPosition = (position: LeadershipPosition): DisplayPosition => ({
  organization: position.organizationName,
  position: position.role,
  startDate: formatMonthYear(position.startDate),
  endDate: position.endDate ? formatMonthYear(position.endDate) : 'present',
  logoUrl: position.logoUrl,
});

export const LeadershipPositionsSection = ({
  variant = 'student',
}: {
  variant?: 'student' | 'admin';
}): ReactElement | null => {
  const coCurricularApi = useCoCurricularApi();
  const [positions, setPositions] = useState<DisplayPosition[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      const result = await coCurricularApi.getLeadershipPositions();

      if (!cancelled && result.success)
        setPositions(result.value.positions.map(toDisplayPosition));

      if (!cancelled) setLoading(false);
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [coCurricularApi]);

  useEffect(() => {
    let cancelled = false;

    const fetchSummary = async () => {
      const result = await coCurricularApi.getSummary();

      if (!cancelled && result.success)
        setTotalCount(result.value.totalLeadershipPositions);

    };

    fetchSummary();

    return () => {
      cancelled = true;
    };
  }, [coCurricularApi]);

  if (loading || positions.length === 0) return null;

  return (
    <Box sx={{ marginBottom: '48px' }}>
      <Box display="flex" alignItems="center" gap="16px" mb={2}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            color: '#1F1F1F',
          }}
        >
          Leadership Positions
        </Typography>
        <SectionCountBadge count={totalCount} />
      </Box>
      <Typography
        sx={{
          fontSize: '14px',
          color: '#6B6B6B',
          marginBottom: '24px',
        }}
      >
        {variant === 'admin'
          ? 'Officer roles and elected positions held by this student. Terms shown as recorded by the organization advisor.'
          : 'Officer roles and elected positions are important to your co-curricular transcript.'}
      </Typography>

      <Grid container spacing={2}>
        {positions.map((position, index) => (
          <Grid item xs={12} md={6} key={index}>
            <LeadershipPositionCard {...position} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
